/* eslint-disable no-nested-ternary */
import { reflect } from '@effector/reflect';
import { Anchor, NumberFormatter } from '@mantine/core';
import { Link } from 'atomic-router-react';
import { FileSearch, Infinity } from 'lucide-react';
import { forwardRef, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { AccountRegistryModel } from 'entities/account';
import { AccountWithWalletsAndPermissions } from 'shared/api';
import { translated } from 'shared/lib';
import { accountRoute } from 'shared/routing';
import { createRegistry, MultiSelect, RegistryRootProps, SearchInput } from 'shared/ui';
import { accountTreeModel } from '../../model';
import cls from './account-registry.module.scss';

export type AccountRegistryProps = RegistryRootProps & {
	withParent?: boolean;
	withTitle?: boolean;
	emptyActionSlot?: ReactNode;
	topBarActionSlot?: ReactNode;
	tableTdActionSlot?: (account: AccountWithWalletsAndPermissions) => ReactNode;
};

export const createAccountRegistry = (model: AccountRegistryModel) => {
	const Registry = createRegistry(model);

	const NameFilter = reflect({
		view: SearchInput<'input'>,
		bind: {
			'data-test-id': 'name-search-input',
			label: translated('account-registry.filters.name.label'),
			placeholder: translated('account-registry.filters.name.placeholder'),
			className: cls.accountRegistryNameFilter,
			value: model.filters.name.$value,
			onChange: (e) => model.filters.name.setValue(e.target.value),
		},
	});

	const CurrenciesFilter = reflect({
		view: MultiSelect,
		bind: {
			id: 'currencies-filter',
			label: translated('account-registry.filters.currencies.label'),
			placeholder: translated('account-registry.filters.currencies.placeholder'),
			className: cls.accountRegistryCurrenciesFilter,
			data: accountTreeModel.$currencies,
			value: model.filters.currencies.$value,
			onChange: (value) => model.filters.currencies.setValue(value),
			searchable: true,
		},
	});

	const AccountRegistry = forwardRef<HTMLDivElement, AccountRegistryProps>(
		(
			{
				withParent = true,
				withTitle = true,
				emptyActionSlot,
				topBarActionSlot,
				tableTdActionSlot,
				...props
			},
			ref,
		) => {
			const { t } = useTranslation();

			return (
				<Registry.Root
					ref={ref}
					{...props}
				>
					{withTitle && <Registry.Title>{t('account-registry.title')}</Registry.Title>}
					<Registry.TopBar className={cls.accountRegistryTopBar}>
						<Registry.Filters className={cls.accountRegistryFilters}>
							<NameFilter />
							<CurrenciesFilter />
						</Registry.Filters>
						<div className={cls.accountRegistryActions}>{topBarActionSlot}</div>
					</Registry.TopBar>
					<Registry.Table>
						<Registry.Table.Colgroup>
							{withParent && <Registry.Table.Col />}
							<Registry.Table.Col />
							<Registry.Table.Col />
							<Registry.Table.Col />
							<Registry.Table.Col />
							<Registry.Table.Col />
						</Registry.Table.Colgroup>
						<Registry.Table.Thead>
							<Registry.Table.Tr>
								<Registry.Table.ThSorter field='name'>
									{t('account-registry.table-columns.name')}
								</Registry.Table.ThSorter>
								{withParent && (
									<Registry.Table.ThSorter field='parent'>
										{t('account-registry.table-columns.parent-account')}
									</Registry.Table.ThSorter>
								)}
								<Registry.Table.ThSorter field='currencies'>
									{t('account-registry.table-columns.currencies')}
								</Registry.Table.ThSorter>
								<Registry.Table.ThSorter field='balance'>
									{t('account-registry.table-columns.balance')}
								</Registry.Table.ThSorter>
								<Registry.Table.ThSorter field='note'>
									{t('account-registry.table-columns.note')}
								</Registry.Table.ThSorter>
								<Registry.Table.Th>{t('account-registry.table-columns.actions')}</Registry.Table.Th>
							</Registry.Table.Tr>
						</Registry.Table.Thead>
						<Registry.Table.Tbody>
							<Registry.Table.TrList
								getProps={(row) => ({ variant: row.isLocked ? 'locked' : 'default' })}
							>
								{(row) => (
									<>
										<Registry.Table.Td>
											<Anchor
												data-test-id={`account-registry-account-link-${row.id}`}
												component={Link}
												to={accountRoute}
												query={{ id: row.id }}
											>
												{row.name}
											</Anchor>
										</Registry.Table.Td>
										{withParent && (
											<Registry.Table.Td>
												{row.parent && (
													<Anchor
														data-test-id={`account-registry-parent-account-link-${row.parent.id}`}
														component={Link}
														to={accountRoute}
														query={{ id: row.parent.id }}
													>
														{row.parent.name ?? ''}
													</Anchor>
												)}
											</Registry.Table.Td>
										)}
										<Registry.Table.Td>
											{row.wallets.map((wallet) => wallet.currency).join(', ')}
										</Registry.Table.Td>
										<Registry.Table.Td>
											{row.wallets.length === 1 ? (
												row.wallets[0].balance !== null ? (
													<NumberFormatter value={row.wallets[0].balance} />
												) : (
													<Infinity style={{ fontSize: 16 }} />
												)
											) : (
												''
											)}
										</Registry.Table.Td>
										<Registry.Table.Td>{row.note}</Registry.Table.Td>
										<Registry.Table.Td>{tableTdActionSlot?.(row)}</Registry.Table.Td>
									</>
								)}
							</Registry.Table.TrList>
						</Registry.Table.Tbody>
					</Registry.Table>
					<Registry.BottomBar>
						<Registry.Pagination />
					</Registry.BottomBar>
					<Registry.Empty
						icon={<FileSearch />}
						title={t('account-registry.empty.title')}
						message={t('account-registry.empty.message')}
					>
						{emptyActionSlot}
					</Registry.Empty>
					<Registry.Table.Empty
						icon={<FileSearch />}
						title={t('account-registry.table-empty.title')}
						message={t('account-registry.table-empty.message')}
					/>
				</Registry.Root>
			);
		},
	);

	return AccountRegistry;
};
